import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ShoppingCart, LogOut, User } from "lucide-react";
import { AppButton, AppDrawer } from "@/components/app";
import Logo from "@/components/icons/Logo";
import { ROUTES } from "@/lib/routes";
import { useApp } from "@/context/AppContext";
import { LoginModal } from "@/components/landing/LoginModal";

export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, logout, cartCount } = useApp();

  interface NavItem {
    label: string;
    href: string;
    isRoute?: boolean;
  }

  const baseNavItems: NavItem[] = [
    { label: "Features", href: "#features" },
    { label: "Testimonials", href: "#testimonials" },
    // { label: "FAQ", href: "#faq" },
  ];

  const navItems = isLoggedIn
    ? [
        ...baseNavItems,
        { label: "Products", href: ROUTES.PRODUCTS, isRoute: true },
      ]
    : baseNavItems;

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, [location]);

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
    isRoute?: boolean,
  ) => {
    e.preventDefault();
    setIsOpen(false);

    if (isRoute) {
      navigate(href);
      return;
    }

    if (location.pathname !== ROUTES.HOME) {
      navigate(ROUTES.HOME + href);
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  const isActive = (item: { href: string; isRoute?: boolean }) => {
    if (item.isRoute) return location.pathname === item.href;
    return location.hash === item.href;
  };

  return (
    <>
      <header className="">
        <div className="wrapper">
          <div className="xl:py-[50px] lg:py-10 py-4 md:py-8">
            <div className="flex justify-between items-center">
              {/* Logo */}
              <Link
                to="/"
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              >
                <div className="w-[172px] lg:w-[230px]">
                  <Logo />
                </div>
              </Link>

              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center xl:gap-10 gap-4">
                {navItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href, item.isRoute)}
                    className={`relative text-foreground hover:opacity-70 transition-colors text-lg font-semibold pb-1
                      after:content-[''] after:absolute after:bottom-0 after:left-0 after:h-[2px] after:bg-primary
                      after:transition-all after:duration-300 after:ease-out
                      ${
                        isActive(item)
                          ? "after:w-full text-primary"
                          : "after:w-0 hover:after:w-full"
                      }`}
                  >
                    {item.label}
                  </a>
                ))}
              </nav>

              {/* Desktop CTA Buttons */}
              <div className="hidden lg:flex items-center gap-3">
                {isLoggedIn ? (
                  <>
                    {/* Cart Icon */}
                    <button
                      onClick={() => navigate(ROUTES.CART)}
                      className="relative p-2 rounded-full hover:bg-muted transition-colors"
                      aria-label="Cart"
                    >
                      <ShoppingCart className="h-6 w-6 text-foreground" />
                      {cartCount > 0 && (
                        <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">
                          {cartCount > 9 ? "9+" : cartCount}
                        </span>
                      )}
                    </button>

                    {/* User + Logout */}
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium">
                        <User className="h-4 w-4" />
                        <span>Admin</span>
                      </div>
                      <AppButton
                        variant="text"
                        size="sm"
                        onClick={logout}
                        startIcon={<LogOut className="h-4 w-4" />}
                      >
                        Logout
                      </AppButton>
                    </div>
                  </>
                ) : (
                  <>
                    <AppButton
                      variant="contained"
                      size="lg"
                      onClick={() => setShowLogin(true)}
                    >
                      Login
                    </AppButton>
                    <AppButton variant="outlined" size="lg" asChild>
                      <Link to={ROUTES.CONTACT_US}>Contact Us</Link>
                    </AppButton>
                  </>
                )}
              </div>

              {/* Mobile: cart icon (when logged in) + menu button */}
              <div className="lg:hidden flex items-center gap-2">
                {isLoggedIn && (
                  <button
                    onClick={() => navigate(ROUTES.CART)}
                    className="relative p-2 rounded-full hover:bg-muted transition-colors"
                    aria-label="Cart"
                  >
                    <ShoppingCart className="h-5 w-5 text-foreground" />
                    {cartCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-bold">
                        {cartCount > 9 ? "9+" : cartCount}
                      </span>
                    )}
                  </button>
                )}
                <AppButton
                  onClick={() => setIsOpen(!isOpen)}
                  aria-label="Toggle menu"
                  size="icon"
                >
                  {isOpen ? <X size={24} /> : <Menu size={24} />}
                </AppButton>
              </div>
            </div>
          </div>

          {/* Mobile Navigation Drawer */}
          <AppDrawer
            open={isOpen}
            onClose={() => setIsOpen(false)}
            direction="right"
            size={320}
          >
            <div className="p-6">
              {/* Header with Logo and Close Button */}
              <div className="flex items-center justify-between mb-8">
                <div className="w-[115px] md:w-[172px] lg:w-[230px]">
                  <Logo />
                </div>
                <AppButton
                  onClick={() => setIsOpen(false)}
                  aria-label="Close menu"
                  size="icon"
                  variant="text"
                >
                  <X size={24} />
                </AppButton>
              </div>

              {/* Navigation Links */}
              <nav className="flex flex-col gap-2 mb-6">
                {navItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href, item.isRoute)}
                    className={`text-foreground hover:text-primary transition-all text-base font-medium px-5 py-3 bg-muted shadow-sm rounded-lg duration-150 ${
                      isActive(item)
                        ? "bg-primary text-white"
                        : "hover:bg-primary hover:text-white"
                    }`}
                  >
                    {item.label}
                  </a>
                ))}
              </nav>

              {/* CTA Buttons */}
              <div className="flex flex-col gap-3 pt-6 border-t border-border-light">
                {isLoggedIn ? (
                  <>
                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-primary/10">
                      <User className="h-4 w-4 text-primary" />
                      <span className="text-primary text-sm font-medium">
                        Admin
                      </span>
                    </div>
                    <AppButton
                      variant="outlined"
                      size="lg"
                      fullWidth
                      onClick={() => {
                        logout();
                        setIsOpen(false);
                      }}
                      startIcon={<LogOut className="h-4 w-4" />}
                    >
                      Logout
                    </AppButton>
                  </>
                ) : (
                  <>
                    <AppButton
                      size="lg"
                      fullWidth
                      onClick={() => {
                        setIsOpen(false);
                        setShowLogin(true);
                      }}
                    >
                      Login
                    </AppButton>
                    <AppButton variant="outlined" size="lg" fullWidth asChild>
                      <Link
                        to={ROUTES.CONTACT_US}
                        onClick={() => setIsOpen(false)}
                      >
                        Contact Us
                      </Link>
                    </AppButton>
                  </>
                )}
              </div>
            </div>
          </AppDrawer>
        </div>
      </header>

      <LoginModal open={showLogin} onClose={() => setShowLogin(false)} />
    </>
  );
}
